import type { DashboardTableSortDirection, DashboardTableSortState } from '@fluenceur/types';
import { Button, HStack, Table, Text } from '@chakra-ui/react';
import type { ComponentProps, ReactNode } from 'react';
import { ArrowSeparateVertical, NavArrowDown, NavArrowUp } from 'iconoir-react';

import { getNextDashboardTableSortState } from './dashboard-table-utils';

interface SortableColumnHeaderProps<SortBy extends string>
  extends Omit<ComponentProps<typeof Table.ColumnHeader>, 'onChange' | 'children'> {
  column: SortBy;
  label: ReactNode;
  sort: DashboardTableSortState<SortBy>;
  defaultSort: DashboardTableSortState<SortBy>;
  onSortChange: (sort: DashboardTableSortState<SortBy>) => void;
  defaultDirection?: DashboardTableSortDirection;
  disabled?: boolean;
}

export const SortableColumnHeader = <SortBy extends string>({
  column,
  label,
  sort,
  defaultSort,
  onSortChange,
  defaultDirection = 'desc',
  disabled = false,
  ...headerProps
}: SortableColumnHeaderProps<SortBy>) => {
  const isActive = sort.sortBy === column;
  const Icon = isActive
    ? sort.sortDirection === 'asc'
      ? NavArrowUp
      : NavArrowDown
    : ArrowSeparateVertical;
  const justifyContent = getHeaderButtonJustifyContent(headerProps.textAlign);

  return (
    <Table.ColumnHeader
      bg={isActive ? 'gray.50/70' : undefined}
      aria-sort={toAriaSort(isActive, sort.sortDirection)}
      {...headerProps}
    >
      <Button
        type="button"
        variant="plain"
        disabled={disabled}
        h="auto"
        minH={0}
        minW={0}
        w="full"
        p={0}
        justifyContent={justifyContent}
        fontSize="inherit"
        fontWeight="inherit"
        lineHeight="inherit"
        color="inherit"
        letterSpacing="inherit"
        textAlign="inherit"
        textTransform="inherit"
        onClick={() =>
          onSortChange(getNextDashboardTableSortState(sort, column, defaultSort, defaultDirection))
        }
      >
        <HStack gap={1} minW={0} maxW="full">
          <Text as="span" truncate>
            {label}
          </Text>
          <Icon
            width={14}
            height={14}
            opacity={isActive ? 1 : 0.45}
            aria-hidden
            style={{ flexShrink: 0 }}
          />
        </HStack>
      </Button>
    </Table.ColumnHeader>
  );
};

function getHeaderButtonJustifyContent(
  textAlign: ComponentProps<typeof Table.ColumnHeader>['textAlign']
): ComponentProps<typeof Button>['justifyContent'] {
  if (textAlign === 'center') {
    return 'center';
  }

  if (textAlign === 'right' || textAlign === 'end') {
    return 'flex-end';
  }

  return 'flex-start';
}

function toAriaSort(
  isActive: boolean,
  direction: DashboardTableSortDirection
): ComponentProps<typeof Table.ColumnHeader>['aria-sort'] {
  if (!isActive) {
    return 'none';
  }

  return direction === 'asc' ? 'ascending' : 'descending';
}
